import { useQuery, type QueryState } from "../analytics";
import { workspaceKeyPrefix } from "../identity/active";
import { useInsightClient } from "./client";
import type { Insight } from "./types";

/* Reads use the analytics useQuery cache, keyed under the active workspace
 * (the gateway scopes insights by X-Workspace-Id). Mutations (apply/dismiss)
 * don't go through here — callers invoke the client directly then bump
 * `version` to force a refetch, mirroring src/agents/hooks.ts. */

/** How many published insights each agent has, keyed by agent_id (OPSS-292).
 *  One workspace-wide read, so the roster doesn't fan out per row. */
export function useInsightCounts(
  version = 0,
): QueryState<Record<string, number>> {
  const client = useInsightClient();
  return useQuery(
    async () => {
      const rows = await client.listAll();
      const counts: Record<string, number> = {};
      for (const row of rows) {
        if (row.state !== "published") continue;

        counts[row.agent_id] = (counts[row.agent_id] ?? 0) + 1;
      }
      return counts;
    },
    `${workspaceKeyPrefix()}insightCounts:v${version}`,
    "loadInsightCounts",
  );
}

/** Published insights for an agent (OPSS-210), for the recommendations rail. */
export function useInsights(
  agentId: string,
  version = 0,
): QueryState<Insight[]> {
  const client = useInsightClient();
  return useQuery(
    () => client.list(agentId),
    `${workspaceKeyPrefix()}insights:${agentId}:v${version}`,
    "loadInsights",
  );
}
